import React, { useState, useEffect, useCallback } from 'react';
import Box from '@amzn/meridian/box';
import DatePicker from '@amzn/meridian/date-picker';
import Select, { SelectOption } from '@amzn/meridian/select';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Image from '@amzn/meridian/image';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import { useAppDispatch, useAppSelector } from '../../store';
import { setStationCode, setPlanId, setShiftPlanData } from '../../store/slices/shiftPlanSlice';
import { setShiftPlans, selectAllShiftPlans } from '../../store/slices/GetAllShiftPlansSlice';
import { createShiftPlan, getAllShiftPlans, getClockWindowNameBasedOnSelectedOption, getPlanTypeBasedOnSelectedTab } from '../../services/shiftPlanService';
import { isFeatureEnabled } from '../../services/featureEnabledService';
import { PlanVersion } from '../../types/shiftPlan';
import { getTodayDate, getYesterdayDate, getFutureDate } from './models';
import NoExistingShiftPlan from './NoExistingShiftPlan';
import CurrentPlanSummaryGated from './CurrentPlanSummary/CurrentPlanSummaryGated';
import raptorSvg from '../../assets/images/raptor.svg';
import './index.scss';
import { COULD_NOT_PROCEEED_WITH_PLAN_CREATION, ErrorCode, UNABLE_TO_CREATE_PLAN, DEFAULT_SHIFT_OPTIONS } from '../../utils/constants';
import type { ShiftOption } from '../../utils/constants';

import {useNavigate} from "react-router-dom";

interface TabOption {
  value: string;
  label: string;
}

interface ShiftPlanningProps {
  onDateChange?: (date: string) => void;
  onShiftChange?: (shift: string) => void;
  onTabChange?: (tab: string) => void;
  shiftOptions?: ShiftOption[];
  tabOptions?: TabOption[];
  defaultTab?: string;
}

const defaultTabOptions: TabOption[] = [
  { value: 'pdp', label: 'Prior Day (PDP)' },
  { value: 'sos', label: 'Start of Shift (SOS)' },
];

// Feature flag for the refactored published shift plan page (UTR-REFLEX-1163),
// gated via the RnRRCCFacadeService IsFeatureEnabled AppConfig mechanism.
const PUBLISHED_SHIFT_PLAN_V2_FEATURE = 'RCC_PUBLISHED_SHIFT_PLAN_V2';

const ShiftPlanning: React.FC<ShiftPlanningProps> = ({
  onDateChange,
  onShiftChange,
  onTabChange,
  shiftOptions = DEFAULT_SHIFT_OPTIONS,
  tabOptions = defaultTabOptions,
  defaultTab = 'sos',
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const shiftPlans = useAppSelector(selectAllShiftPlans);

  const [selectedDate, setSelectedDate] = useState<string>(getTodayDate());
  const [selectedShift, setSelectedShift] = useState<string>('day');
  const [selectedTab, setSelectedTab] = useState<string>(defaultTab);

  // Modal state — owned here so both children can trigger it via callback
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isTakingOver, setIsTakingOver] = useState<boolean>(false);
  const [takeOverMessage, setTakeOverMessage] = useState<string>('');

  // Read URL parameters and dispatch to Redux
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stationCode = urlParams.get('stationCode');
    const planId = urlParams.get('planId');

    if (stationCode) {
      dispatch(setStationCode({ stationCode }));
    }

    if (planId) {
      dispatch(setPlanId({ planId }));
    }
  }, [dispatch]);

  // Integration check for the RCC_PUBLISHED_SHIFT_PLAN_V2 feature flag: resolve it
  // once via the facade IsFeatureEnabled API and log the result. Validates the
  // end-to-end flag wiring (UTR-REFLEX-1163) ahead of consuming it for the V2 UI.
  useEffect(() => {
    isFeatureEnabled(PUBLISHED_SHIFT_PLAN_V2_FEATURE)
      .then((enabled) => {
        console.log(`[ShiftPlanning] ${PUBLISHED_SHIFT_PLAN_V2_FEATURE} enabled: ${enabled}`);
      })
      .catch((error) => {
        console.error(`[ShiftPlanning] Failed to resolve ${PUBLISHED_SHIFT_PLAN_V2_FEATURE}:`, error);
      });
  }, []);

  // Fetch shift plans when date, shift, or tab changes
  useEffect(() => {
    const fetchShiftPlans = async () => {
      if (selectedDate && selectedShift) {
        try {
          const planType = getPlanTypeBasedOnSelectedTab(selectedTab);
          const response = await getAllShiftPlans(selectedDate, getClockWindowNameBasedOnSelectedOption(selectedShift), planType);
          if (response && response.shiftPlans) {
            dispatch(setShiftPlans({
              shiftPlans: response.shiftPlans
            }));
          } else {
            dispatch(setShiftPlans({
              shiftPlans: []
            }));
          }
        } catch (error) {
          console.error('Error fetching shift plans:', error);
          dispatch(setShiftPlans({
            shiftPlans: []
          }));
        }
      }
    };

    fetchShiftPlans();
  }, [selectedDate, selectedShift, selectedTab, dispatch]);

  /**
   * Shared success handler — called by both children on successful plan creation,
   * and by handleTakeOver after a successful take-over.
   * Centralised here to avoid duplicating dispatch + navigate in both children.
   */
  const handlePlanSuccess = useCallback(
    (planId: string) => {
      dispatch(setShiftPlanData({ date: selectedDate, shift: selectedShift, plan: selectedTab }));
      dispatch(setPlanId({ planId }));
      navigate('/shift-plan/roster');
    },
    [dispatch, navigate, selectedDate, selectedShift, selectedTab]
  );

  /**
   * Called by either child when createShiftPlan returns PLAN_OWNED_BY_ANOTHER_USER.
   */
  const handleNeedsTakeOver = useCallback((displayMessage: string) => {
    setTakeOverMessage(displayMessage);
    setModalOpen(true);
  }, []);

  /**
   * "Take Over" button handler.
   * Retries createShiftPlan with takeOverPlan: true using local state directly.
   */
  const handleTakeOver = async () => {
    setIsTakingOver(true);

    const result = await createShiftPlan(
      selectedDate,
      getClockWindowNameBasedOnSelectedOption(selectedShift),
      getPlanTypeBasedOnSelectedTab(selectedTab),
      PlanVersion.STATIC_V1,
      true // takeOverPlan: true
    );
    setIsTakingOver(false);
    if (result.errorDetails?.errorCode === ErrorCode.PLAN_OWNED_BY_ANOTHER_USER) {
      handleNeedsTakeOver(result.errorDetails.errorMessage);
    } else if (result.status !== 'FAILED' && result.planId) {
      setModalOpen(false);
      handlePlanSuccess(result.planId);
    } else {
      setModalOpen(false);
    }
  };

  const handleClose = () => setModalOpen(false);

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    if (onDateChange) {
      onDateChange(date);
    }
  };

  const handleShiftChange = (shift: string) => {
    setSelectedShift(shift);
    if (onShiftChange) {
      onShiftChange(shift);
    }
  };

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  const hasShiftPlans = shiftPlans && Array.isArray(shiftPlans) && shiftPlans.length > 0;

  return (
    <Box spacingInset="m" height="100%">
      <Heading level={1}>Shift Plan</Heading>
      <Text color="secondary">This page allows you to run and view the next shift plan and accept or override it.</Text>

      {/* Input controls box */}
      <Box className="shift-inputs-box" spacingInset="400" type="outline">
        <Row spacing="500" alignmentVertical="bottom" widths="fit">
          <Column spacing="200">
            <Text type="b200" tag="label">Date</Text>
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
            />
          </Column>

          <Column spacing="200">
            <Text type="b200" tag="label">Shift</Text>
            <Select
              value={selectedShift}
              onChange={handleShiftChange}
            >
              {shiftOptions.map((option) => (
                <SelectOption key={option.value} value={option.value} label={option.label} />
              ))}
            </Select>
          </Column>
        </Row>
      </Box>

      {/* Tabs with full-width underline */}
      <Box spacingInset="300 none">
        <TabGroup value={selectedTab} onChange={handleTabChange} fill="line">
          {tabOptions.map((option) => (
            <Tab key={option.value} value={option.value}>
              {option.label}
            </Tab>
          ))}
        </TabGroup>
      </Box>

      {/* Plan status box */}
      <Box className="shift-plan-status-box" spacingInset="400" type="outline">
        {hasShiftPlans ? (
          <CurrentPlanSummaryGated
            shiftPlans={shiftPlans}
            selectedTab={selectedTab}
            selectedDate={selectedDate}
            selectedShift={selectedShift}
            onNeedsTakeOver={handleNeedsTakeOver}
            onPlanSuccess={handlePlanSuccess}
          />
        ) : (
          <NoExistingShiftPlan
            selectedTab={selectedTab}
            selectedDate={selectedDate}
            selectedShift={selectedShift}
            onNeedsTakeOver={handleNeedsTakeOver}
            onPlanSuccess={handlePlanSuccess}
          />
        )}
      </Box>

      {/* Conflict modal — only mounts when a plan already exists
      for the same FC for the same type of plan and same date and shift */}
      <Modal
            title= {UNABLE_TO_CREATE_PLAN}
            open={modalOpen}
            onClose={handleClose}
            scrollContainer="viewport"
            aria-describedby="notification-modal-description"
          >
            <Alert
              type= 'warning'
              size="large"
            >
              <Text id="notification-modal-description">
                {COULD_NOT_PROCEEED_WITH_PLAN_CREATION}
              </Text>
            </Alert>
            <Text id="modal-description">{takeOverMessage}</Text>
            <ModalFooter>
              <Row alignmentHorizontal="end" widths="fit">
                <Button onClick={handleTakeOver} disabled={isTakingOver}>
                  {isTakingOver ? 'Taking Over...' : 'Take Over'}
                </Button>
                <Button onClick={handleClose} type="secondary">
                  Cancel
                </Button>
              </Row>
            </ModalFooter>
      </Modal>

      {/* Decorative Raptor SVG in lower right corner */}
      <div className="decorative-raptor">
        <Image
          src={raptorSvg}
          viewportHeight={200}
          viewportAspectRatio={1}
        >
          ReLo Raptor
        </Image>
      </div>
    </Box>
  );
};

export default ShiftPlanning;

import './index.scss';